import { Box, Typography } from "@mui/material";
import { ResponsiveLine } from "@nivo/line";
import { useMemo } from "react";
import useColors from "../../api/hooks/useColors";

// Custom legend component with rounded corners
const CustomLegend = ({ data, colors }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        gap: 5,
        marginBottom: 2,
        marginLeft: 6,
      }}
    >
      {data.map((item) => (
        <Box
          key={item.id}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "3px",
              backgroundColor: item.color,
            }}
          />
          <Typography variant="caption" sx={{ color: colors.grey_600 }}>
            {item.id}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// Generate mock data for the trend chart
const generateMockData = (colors) => {
  // Get current date
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Generate 12 months of data (past 9 months + current month + 2 future months)
  const months = [];
  const yourPostureData = [];
  const industryAverageData = [];

  // Find the "Breeze deployed" month (7 months before current)
  const breezeDeployedIndex = (currentMonth - 7 + 12) % 12;

  for (let i = 0; i < 12; i++) {
    // Calculate the month index, going back 9 months from current
    const monthIndex = (currentMonth - 9 + i + 12) % 12;
    const year = currentMonth - 9 + i < 0 ? currentYear - 1 : currentYear;

    // Create date for this month
    const date = new Date(year, monthIndex, 1);
    const monthLabel =
      date.toLocaleString("default", { month: "short" }) + " 1";
    months.push(monthLabel);

    // Calculate position relative to Breeze deployment
    const monthsSinceDeployment =
      i - (breezeDeployedIndex - ((currentMonth - 9 + 12) % 12));

    // Initialize score variables
    let postureScore;
    let industryScore;

    if (i < 9) {
      // Past data (including current month)
      if (monthIndex < breezeDeployedIndex && year <= currentYear) {
        // Before Breeze deployment - fluctuating around lower values
        postureScore = 82 + Math.floor(Math.random() * 3); // 82-84
        industryScore = 80 + Math.floor(Math.random() * 3); // 80-82, below posture score
      } else {
        // After Breeze deployment - clear upward trend
        // Start at 84-85 right after deployment and gradually increase to 91-93 by current month
        const progressRatio =
          monthsSinceDeployment /
          (9 - (breezeDeployedIndex - ((currentMonth - 9 + 12) % 12)));
        const baseScore = 84;
        const maxIncrease = 9; // Maximum increase from base to current
        const increase = Math.floor(maxIncrease * progressRatio);

        // Add slight randomness but maintain the upward trend
        const randomVariation = Math.floor(Math.random() * 2); // 0-1
        postureScore = baseScore + increase + randomVariation;

        // Industry average grows more slowly and stays below posture score
        industryScore =
          82 + Math.floor(progressRatio * 3) + Math.floor(Math.random() * 2);
      }
    } else {
      // Future projected data - continuing the upward trend
      const monthsAfterCurrent = i - 9;
      postureScore = 93 + monthsAfterCurrent + Math.floor(Math.random() * 2); // 93-96
      industryScore =
        88 +
        Math.floor(monthsAfterCurrent * 0.5) +
        Math.floor(Math.random() * 2); // 88-91, below posture score
    }

    yourPostureData.push({ x: monthLabel, y: postureScore });
    industryAverageData.push({ x: monthLabel, y: industryScore });
  }

  // Store the "Breeze deployed" month for annotations
  const breezeDeployedMonth =
    months[Math.abs(breezeDeployedIndex - ((currentMonth - 9 + 12) % 12))];

  // Store the current month for annotations
  const currentMonthLabel = months[9]; // Current month is at index 9

  // Return data with metadata for annotations
  return {
    data: [
      {
        id: "Industry Average",
        data: industryAverageData,
        color: colors.grey_400, // Medium gray color
      },
      {
        id: "Your Posture",
        data: yourPostureData,
        color: colors.dataviz_blue, // Light blue color
      },
    ],
    metadata: {
      breezeDeployedMonth,
      currentMonthLabel,
    },
  };
};

// Custom layer component for vertical annotation lines
const VerticalAnnotations = ({ data, xScale, yScale, height, colors }) => {
  // Safety check
  if (
    !data ||
    !data.data ||
    !data.data[0] ||
    !data.data[0].data ||
    !xScale ||
    !height ||
    !colors
  ) {
    return null;
  }

  // Use useMemo for expensive computations
  const annotations = [];

  // Find deployment month and current month from metadata if available
  const deploymentMonth = data.metadata?.breezeDeployedMonth;
  const currentMonthLabel = data.metadata?.currentMonthLabel;

  // If metadata is not available, use default approach
  if (!deploymentMonth) {
    // Find a point approximately 3 months before current (which is at index 9)
    const deploymentPoint = data.data[0].data[6]; // 3 months before current

    if (deploymentPoint && typeof deploymentPoint.x !== "undefined") {
      try {
        const deploymentXPos = xScale(deploymentPoint.x);

        // Only add if we got a valid position
        if (!isNaN(deploymentXPos)) {
          annotations.push({
            x: deploymentXPos,
            label: "Breeze Deployed",
          });
        }
      } catch (error) {
        console.error(
          "Error calculating deployment annotation position:",
          error
        );
      }
    }
  } else {
    // Use the metadata
    try {
      const deploymentXPos = xScale(deploymentMonth);

      // Only add if we got a valid position
      if (!isNaN(deploymentXPos)) {
        annotations.push({
          x: deploymentXPos,
          label: "Breeze Deployed",
        });
      }
    } catch (error) {
      console.error("Error calculating deployment annotation position:", error);
    }
  }

  // Find the current month (index 9)
  const currentMonthIndex = 9;

  // Use the metadata currentMonthLabel if available, otherwise use the point at index 9
  if (currentMonthLabel) {
    try {
      const currentXPos = xScale(currentMonthLabel);

      // Only add if we got a valid position
      if (!isNaN(currentXPos)) {
        annotations.push({
          x: currentXPos,
          label: "Today",
        });
      }
    } catch (error) {
      console.error(
        "Error calculating current month annotation position:",
        error
      );
    }
  } else {
    // Fallback to using the data point
    const currentPoint = data.data[0].data[currentMonthIndex];

    if (currentPoint && typeof currentPoint.x !== "undefined") {
      try {
        const currentXPos = xScale(currentPoint.x);

        // Only add if we got a valid position
        if (!isNaN(currentXPos)) {
          annotations.push({
            x: currentXPos,
            label: "Today",
          });
        }
      } catch (error) {
        console.error(
          "Error calculating current month annotation position:",
          error
        );
      }
    }
  }

  // If no valid annotations, return null
  if (annotations.length === 0) return null;

  return (
    <g>
      {annotations.map((annotation, index) => (
        <g key={`annotation-${index}`}>
          <line
            x1={annotation.x}
            x2={annotation.x}
            y1={0}
            y2={height}
            stroke={colors.grey_500}
            strokeWidth={0.5}
            strokeDasharray="3 3"
          />
          <text
            x={annotation.x}
            y={10}
            textAnchor="middle"
            fill={colors.grey_600}
            fontSize={10}
            fontWeight={500}
            fontFamily="Poppins"
          >
            {annotation.label}
          </text>
        </g>
      ))}
    </g>
  );
};
// https://nivo.rocks/storybook/?path=/story/line--custom-line-style
const DashedSolidLine = ({ series, lineGenerator, xScale, yScale }) => {
  return series.map(({ id, data, color }) => {
    // Industry Average should always be dashed
    if (id === "Industry Average") {
      return (
        <path
          key={id}
          d={lineGenerator(
            data.map((d) => ({
              x: xScale(d.data.x),
              y: yScale(d.data.y),
            }))
          )}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeDasharray="5, 5"
        />
      );
    }

    // For "Your Posture" line, transition from solid to dashed at "Today"
    const todayIndex = 9;

    // Split the data into two parts: before/including today, and after today
    const pastData = data.slice(0, todayIndex + 1);
    const futureData = data.slice(todayIndex);

    return (
      <g key={id}>
        {/* Solid line for past/current data */}
        <path
          d={lineGenerator(
            pastData.map((d) => ({
              x: xScale(d.data.x),
              y: yScale(d.data.y),
            }))
          )}
          fill="none"
          stroke={color}
          strokeWidth={2}
        />

        {/* Dashed line for future projected data */}
        {futureData.length > 1 && (
          <path
            d={lineGenerator(
              futureData.map((d) => ({
                x: xScale(d.data.x),
                y: yScale(d.data.y),
              }))
            )}
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeDasharray="5, 5"
          />
        )}
      </g>
    );
  });
};

const PostureTrendChart = ({ height = 400, width = "100%" }) => {
  const colors = useColors();
  const chartData = useMemo(() => generateMockData(colors), []);

  return (
    <Box sx={{ height, width }}>
      <Box sx={{ height: height, width: "100%" }}>
        <ResponsiveLine
          data={chartData.data}
          margin={{ top: 20, right: 30, bottom: 50, left: 50 }}
          xScale={{ type: "point" }}
          yScale={{
            type: "linear",
            min: 80,
            max: 100,
            stacked: false,
          }}
          curve="linear" // Use linear instead of monotoneX for sharper lines
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 0,
            tickPadding: 15,
            tickRotation: 0,
            tickValues: chartData.data[0].data.map((d) => d.x),
            format: (value) => value.replace(" 1", ""),
            legend: "",
            legendOffset: 36,
            legendPosition: "middle",
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 10,
            tickRotation: 0,
            tickValues: [80, 85, 90, 95, 100],
            legend: "",
            legendOffset: -40,
            legendPosition: "middle",
          }}
          enableGridX={false}
          gridYValues={[80, 85, 90, 95, 100]}
          gridXValues={[]}
          enableGridY={true}
          colors={({ id }) =>
            id === "Your Posture" ? colors.dataviz_blue : colors.grey_500
          }
          lineWidth={2}
          pointSize={0} // Remove points for cleaner look
          enablePoints={false}
          pointColor={{ theme: "background" }}
          pointBorderWidth={1}
          pointBorderColor={{ from: "serieColor" }}
          pointLabelYOffset={-12}
          enableSlices="x"
          sliceTooltip={({ slice }) => {
            return (
              <Box
                style={{
                  background: "white",
                  width: "180px",
                  padding: "9px 12px",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                {slice.points.map((point, index) => (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: slice.points[index].seriesColor,
                        width: "10px",
                        height: "10px",
                        borderRadius: "2px",
                      }}
                    ></Box>
                    <Typography variant="overline" color={colors.text_primary}>
                      {point.seriesId}:
                    </Typography>
                    <Typography
                      key={point.id}
                      variant="caption"
                      color={colors.text_primary}
                      sx={{ padding: "3px 0" }}
                    >
                      {point.data.yFormatted}
                    </Typography>
                  </Box>
                ))}
              </Box>
            );
          }}
          useMesh={true}
          theme={{
            axis: {
              domain: {
                line: {
                  stroke: colors.divider2,
                  strokeWidth: 1,
                },
              },
              ticks: {
                line: {
                  stroke: colors.divider2,
                  strokeWidth: 1,
                },
                text: {
                  fill: colors.grey_600,
                  fontSize: 12,
                  fontFamily: "Poppins",
                },
              },
            },
            grid: {
              line: {
                stroke: colors.divider2,
                strokeWidth: 1,
                strokeDasharray: "",
              },
            },
            crosshair: {
              line: {
                stroke: colors.divider2,
                strokeWidth: 1,
                strokeOpacity: 0.5,
                strokeDasharray: "6 6",
              },
            },
          }}
          legends={[]}
          layers={[
            "grid",
            "markers",
            "axes",
            "areas",
            "crosshair",
            "points",
            "slices",
            "mesh",
            DashedSolidLine, // Custom line styling
            // Custom layers for annotations
            ({ xScale, yScale, height }) =>
              VerticalAnnotations({
                data: chartData,
                xScale,
                yScale,
                height,
                colors,
              }),
          ]}
        />
      </Box>
      <CustomLegend data={chartData.data} colors={colors} />
    </Box>
  );
};

export default PostureTrendChart;
